import type {
  CogniPrintProfile,
  ContentFormatPreference,
  SequencingPreference,
  MotivationalDriver,
} from '../types/cogniprint';

export function validateWeightedSum(
  weights: Record<string, number>,
  tolerance = 2,
): boolean {
  const sum = Object.values(weights).reduce((a, b) => a + b, 0);
  return Math.abs(sum - 100) <= tolerance;
}

function clamp(n: unknown, lo: number, hi: number, fallback: number): number {
  const v = typeof n === 'number' && Number.isFinite(n) ? n : fallback;
  return Math.max(lo, Math.min(hi, v));
}

// Clamp each weight to >=0, then rescale so the set sums to exactly 100 (integer),
// fixing rounding drift on the largest weight. Preserves the original keys.
function normalizeWeights<T extends Record<string, number>>(weights: T): T {
  const entries = Object.entries(weights).map(
    ([k, v]) => [k, Math.max(0, typeof v === 'number' && Number.isFinite(v) ? v : 0)] as [string, number],
  );
  const sum = entries.reduce((a, [, v]) => a + v, 0);
  const scaled: Array<[string, number]> =
    sum === 0
      ? entries.map(([k]) => [k, Math.round((100 / entries.length) * 100) / 100])
      : entries.map(([k, v]) => [k, (v / sum) * 100]);
  const rounded = scaled.map(([k, v]) => [k, Math.round(v)] as [string, number]);
  const drift = 100 - rounded.reduce((a, [, v]) => a + v, 0);
  if (drift !== 0 && rounded.length > 0) {
    let maxIdx = 0;
    for (let i = 1; i < rounded.length; i++) if (rounded[i][1] > rounded[maxIdx][1]) maxIdx = i;
    rounded[maxIdx][1] += drift;
  }
  return Object.fromEntries(rounded) as T;
}

/**
 * Structural rails for a profile before it is persisted. Clamps every score to
 * [0,100] and every confidence to [0,1], normalizes the weighted dimensions
 * (content format, sequencing, motivation) to sum to 100, and recomputes
 * overallConfidence. Cheap, deterministic, no downside — stops out-of-range or
 * malformed values (e.g. a hallucinated score of 5000) from ever being stored.
 */
export function sanitizeProfile(profile: CogniPrintProfile): CogniPrintProfile {
  const p: CogniPrintProfile = {
    ...profile,
    contentFormat: {
      score: normalizeWeights({
        visual: clamp(profile.contentFormat?.score?.visual, 0, 100, 20),
        textual: clamp(profile.contentFormat?.score?.textual, 0, 100, 20),
        auditory: clamp(profile.contentFormat?.score?.auditory, 0, 100, 20),
        interactive: clamp(profile.contentFormat?.score?.interactive, 0, 100, 20),
        video: clamp(profile.contentFormat?.score?.video, 0, 100, 20),
      }),
      confidence: clamp(profile.contentFormat?.confidence, 0, 1, 0.5),
    },
    learningApproach: {
      score: {
        theoryApplication: clamp(profile.learningApproach?.score?.theoryApplication, 0, 100, 50),
        reflectiveActive: clamp(profile.learningApproach?.score?.reflectiveActive, 0, 100, 50),
      },
      confidence: clamp(profile.learningApproach?.confidence, 0, 1, 0.5),
    },
    depthTolerance: {
      score: clamp(profile.depthTolerance?.score, 0, 100, 50),
      confidence: clamp(profile.depthTolerance?.confidence, 0, 1, 0.5),
    },
    sequencing: {
      score: normalizeWeights({
        topDown: clamp(profile.sequencing?.score?.topDown, 0, 100, 34),
        bottomUp: clamp(profile.sequencing?.score?.bottomUp, 0, 100, 33),
        modular: clamp(profile.sequencing?.score?.modular, 0, 100, 33),
      }),
      confidence: clamp(profile.sequencing?.confidence, 0, 1, 0.5),
    },
    infoDensity: profile.infoDensity
      ? {
          score: clamp(profile.infoDensity.score, 0, 100, 50),
          confidence: clamp(profile.infoDensity.confidence, 0, 1, 0.5),
        }
      : null,
    motivation: profile.motivation
      ? {
          score: normalizeWeights({
            mastery: clamp(profile.motivation.score?.mastery, 0, 100, 25),
            performance: clamp(profile.motivation.score?.performance, 0, 100, 25),
            social: clamp(profile.motivation.score?.social, 0, 100, 25),
            curiosity: clamp(profile.motivation.score?.curiosity, 0, 100, 25),
          }),
          confidence: clamp(profile.motivation.confidence, 0, 1, 0.5),
        }
      : null,
    viewpointRange: profile.viewpointRange
      ? {
          score: clamp(profile.viewpointRange.score, 0, 100, 50),
          confidence: clamp(profile.viewpointRange.confidence, 0, 1, 0.5),
        }
      : null,
  };
  p.overallConfidence = clamp(calculateOverallConfidence(p), 0, 1, 0.5);
  return p;
}

export function calculateOverallConfidence(profile: CogniPrintProfile): number {
  const confidences = [
    profile.contentFormat.confidence,
    profile.learningApproach.confidence,
    profile.depthTolerance.confidence,
    profile.sequencing.confidence,
  ];

  if (profile.infoDensity) confidences.push(profile.infoDensity.confidence);
  if (profile.motivation) confidences.push(profile.motivation.confidence);
  if (profile.viewpointRange) confidences.push(profile.viewpointRange.confidence);

  return confidences.reduce((a, b) => a + b, 0) / confidences.length;
}

export function isStrengthDimension(
  dimensionKey: string,
  profile: CogniPrintProfile,
): boolean {
  // A dimension is a "strength" if its score is in the top or bottom 15%
  // (most extreme/distinctive — furthest from centre value)
  switch (dimensionKey) {
    case 'depthTolerance':
      return profile.depthTolerance.score >= 85 || profile.depthTolerance.score <= 15;
    case 'infoDensity':
      return profile.infoDensity !== null &&
        (profile.infoDensity.score >= 85 || profile.infoDensity.score <= 15);
    case 'viewpointRange':
      return profile.viewpointRange !== null &&
        (profile.viewpointRange.score >= 85 || profile.viewpointRange.score <= 15);
    case 'contentFormat': {
      const max = Math.max(...Object.values(profile.contentFormat.score));
      return max >= 50; // dominant format > 50% means strong preference
    }
    case 'sequencing': {
      const max = Math.max(...Object.values(profile.sequencing.score));
      return max >= 55;
    }
    case 'motivation': {
      if (!profile.motivation) return false;
      const max = Math.max(...Object.values(profile.motivation.score));
      return max >= 50;
    }
    case 'learningApproach': {
      const { theoryApplication, reflectiveActive } = profile.learningApproach.score;
      return theoryApplication >= 75 || theoryApplication <= 25 ||
        reflectiveActive >= 75 || reflectiveActive <= 25;
    }
    default:
      return false;
  }
}

export function getDominantFormat(format: ContentFormatPreference): string {
  const entries = Object.entries(format) as Array<[string, number]>;
  entries.sort((a, b) => b[1] - a[1]);
  return entries[0][0].charAt(0).toUpperCase() + entries[0][0].slice(1);
}

export function getDominantSequencing(seq: SequencingPreference): string {
  const entries = Object.entries(seq) as Array<[string, number]>;
  entries.sort((a, b) => b[1] - a[1]);
  const labels: Record<string, string> = {
    topDown: 'Top-down',
    bottomUp: 'Bottom-up',
    modular: 'Modular',
  };
  return labels[entries[0][0]] ?? entries[0][0];
}

export function getDominantMotivation(mot: MotivationalDriver): string {
  const entries = Object.entries(mot) as Array<[string, number]>;
  entries.sort((a, b) => b[1] - a[1]);
  return entries[0][0].charAt(0).toUpperCase() + entries[0][0].slice(1);
}

// mergeQuickIntoFull() lived here and was never called from anywhere. Its job —
// stopping a diagnostic run from destroying dimensions it did not assess — is now
// done by mergeProfileForSave() in ./profile-merge.ts, which saveProfile() actually
// uses. Removed 2026-09-05, docs/intent/001-diagnostic-redesign/plan.md A3.
